import type { HubTaskCommentDto } from "@bafe/api-client";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { api } from "../../lib/api.js";
import { presentError } from "../../lib/errors.js";

function formatCommentTime(value: string): string {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleString(undefined, {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

function scrollToCommentCompose(
	panel: HTMLElement | null,
	thread: HTMLElement | null,
	focusTextarea: HTMLTextAreaElement | null,
) {
	if (thread) {
		thread.scrollTop = thread.scrollHeight;
	}
	if (!panel) return;
	const modalBody = panel.closest(".modal-body");
	const modalForm = panel.closest("form");
	const footer = modalForm?.querySelector(".actions");
	if (modalBody instanceof HTMLElement) {
		const bodyRect = modalBody.getBoundingClientRect();
		const panelRect = panel.getBoundingClientRect();
		const footerTop = footer instanceof HTMLElement
			? footer.getBoundingClientRect().top
			: bodyRect.bottom;
		const roomAboveFooter = 8;
		const maxBottom = footerTop - roomAboveFooter;
		if (panelRect.bottom > maxBottom) {
			modalBody.scrollTop += panelRect.bottom - maxBottom;
		}
		const topSlack = 8;
		if (panelRect.top < bodyRect.top + topSlack) {
			modalBody.scrollTop += panelRect.top - bodyRect.top - topSlack;
		}
	} else {
		panel.scrollIntoView({ block: "end", inline: "nearest", behavior: "instant" });
	}
	focusTextarea?.focus({ preventScroll: true });
}

export function TaskDetailComments({
	taskId,
	comments,
	currentUserId,
	canComment = true,
	onCommentsChange,
}: {
	taskId: string;
	comments: HubTaskCommentDto[];
	currentUserId: string;
	canComment?: boolean;
	onCommentsChange: (next: HubTaskCommentDto[]) => void;
}) {
	const [draft, setDraft] = useState("");
	const [pending, setPending] = useState(false);
	const [error, setError] = useState("");
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editDraft, setEditDraft] = useState("");

	const sorted = useMemo(
		() => [...comments].sort((left, right) => left.createdAt.localeCompare(right.createdAt)),
		[comments],
	);
	const threadRef = useRef<HTMLDivElement>(null);
	const composePanelRef = useRef<HTMLDivElement>(null);
	const composeRef = useRef<HTMLTextAreaElement>(null);
	const scrollAfterPostRef = useRef(false);
	const lastCommentId = sorted[sorted.length - 1]?.id;

	useLayoutEffect(() => {
		scrollAfterPostRef.current = false;
	}, [taskId]);

	useLayoutEffect(() => {
		if (!scrollAfterPostRef.current || !lastCommentId) return;
		scrollAfterPostRef.current = false;
		const thread = threadRef.current;
		const panel = composePanelRef.current;
		scrollToCommentCompose(panel, thread, composeRef.current);
		requestAnimationFrame(() => {
			scrollToCommentCompose(composePanelRef.current, threadRef.current, composeRef.current);
		});
	}, [lastCommentId]);

	const submitNew = async () => {
		const body = draft.trim();
		if (!body || pending) return;
		setPending(true);
		setError("");
		try {
			const created = await api.createHubTaskComment(taskId, { body });
			scrollAfterPostRef.current = true;
			onCommentsChange([...comments, created]);
			setDraft("");
		} catch (err) {
			setError(presentError(err).title);
		} finally {
			setPending(false);
		}
	};

	const saveEdit = async (commentId: string) => {
		const body = editDraft.trim();
		if (!body || pending) return;
		setPending(true);
		setError("");
		try {
			const updated = await api.updateHubTaskComment(taskId, commentId, { body });
			onCommentsChange(comments.map((row) => (row.id === commentId ? updated : row)));
			setEditingId(null);
			setEditDraft("");
		} catch (err) {
			setError(presentError(err).title);
		} finally {
			setPending(false);
		}
	};

	const remove = async (commentId: string) => {
		if (pending) return;
		setPending(true);
		setError("");
		try {
			await api.deleteHubTaskComment(taskId, commentId);
			onCommentsChange(comments.filter((row) => row.id !== commentId));
		} catch (err) {
			setError(presentError(err).title);
		} finally {
			setPending(false);
		}
	};

	return (
		<section className="task-detail-block task-comments-section" aria-labelledby="task-comments-label">
			<div className="task-detail-block-icon" aria-hidden="true">
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
				</svg>
			</div>
			<h2 id="task-comments-label" className="task-detail-block-heading task-detail-block-heading-with-icon">
				Comments
			</h2>
			<div className="task-detail-block-body task-comments-body">
				<div
					className="task-comment-thread"
					ref={threadRef}
					data-empty={sorted.length === 0 ? "true" : undefined}
				>
					{sorted.length > 0 ? (
						<ul className="task-comment-list">
							{sorted.map((comment) => {
								const own = comment.authorUserId === currentUserId;
								const editing = editingId === comment.id;
								return (
								<li
									className={`task-comment ${own ? "is-own" : "is-other"}`}
									key={comment.id}
								>
									<div className="task-comment-bubble">
										<div className="task-comment-head">
											<span className="task-comment-author">{comment.authorName}</span>
											<time className="task-comment-time" dateTime={comment.createdAt}>
												{formatCommentTime(comment.createdAt)}
												{comment.updatedAt !== comment.createdAt ? " · edited" : ""}
											</time>
										</div>
										{editing ? (
											<div className="task-comment-edit">
												<textarea
													className="task-comment-input"
													value={editDraft}
													rows={3}
													onChange={(event) => setEditDraft(event.target.value)}
													aria-label="Edit comment"
												/>
												<div className="task-comment-edit-actions">
													<button className="btn ghost" type="button" disabled={pending} onClick={() => {
														setEditingId(null);
														setEditDraft("");
													}}
													>
														Cancel
													</button>
													<button className="btn" type="button" disabled={pending || !editDraft.trim()} onClick={() => void saveEdit(comment.id)}>
														Save
													</button>
												</div>
											</div>
										) : (
											<p className="task-comment-body">{comment.body}</p>
										)}
										{own && !editing ? (
											<div className="task-comment-actions">
												<button
													type="button"
													className="task-comment-action"
													onClick={() => {
														setEditingId(comment.id);
														setEditDraft(comment.body);
													}}
												>
													Edit
												</button>
												<button type="button" className="task-comment-action" onClick={() => void remove(comment.id)}>
													Delete
												</button>
											</div>
										) : null}
									</div>
								</li>
								);
							})}
						</ul>
					) : (
						<p className="hint task-comment-empty">No comments yet.</p>
					)}
				</div>
				{canComment ? <div className="task-comment-compose" ref={composePanelRef}>
					<label className="sr-only" htmlFor="task-comment-draft">Add a comment</label>
					<textarea
						ref={composeRef}
						id="task-comment-draft"
						className="task-comment-input"
						placeholder="Write a comment…"
						rows={3}
						value={draft}
						disabled={pending}
						onChange={(event) => setDraft(event.target.value)}
						onKeyDown={(event) => {
							if (event.key !== "Enter" || event.shiftKey) return;
							event.preventDefault();
							void submitNew();
						}}
					/>
					<div className="task-comment-compose-actions">
						<button className="btn" type="button" disabled={pending || !draft.trim()} onClick={() => void submitNew()}>
							Comment
						</button>
					</div>
				</div> : null}
				{error ? <p className="err task-comment-error">{error}</p> : null}
			</div>
		</section>
	);
}
